import compose from 'lodash/fp/compose'
import castArray from 'lodash/fp/castArray'
import { takeEvery, takeLatest, put, select } from 'redux-saga/effects'

import { selectLoginToken } from '../containers/Login/selectors'

/** @const {String} SAGA_WATCHER_ALL_MODE - take every routine trigger action and executes the saga */
export const SAGA_WATCHER_ALL_MODE = '@@sagas/WATCHER_ALL'

/**
 * @const {String} SAGA_WATCHER_LATEST_MODE - take latest routine trigger action and execute the saga
 * cancelling previous saga task
 */
export const SAGA_WATCHER_LATEST_MODE = '@@sagas/WATCHER_LATEST'

/**
 * Arguments creator to request saga call
 *
 * @callback requestArgsCreator
 * @param {Object} action - action to get request saga args
 */

/**
 * creates sagas for routine
 *
 * @param {Object} config - saga configuration
 * @param {routine} config.routine - routine to complete saga
 * @param {function} config.requestSaga - generator function saga to perform action
 * @param {requestArgsCreator} [config.requestArgsCreator=() => []] - request args creator
 * @param {String} [watcherMode=SAGA_WATCHER_ALL_MODE] - mode to trigger saga
 * @return {Object} object with routine saga and watcher saga
 */
export const createRoutineSaga = ({
  routine,
  requestSaga,
  requestArgsCreator = () => [],
  onSuccessSaga,
  watcherMode = SAGA_WATCHER_ALL_MODE
}) => {
  // should cast as array for pass as ...args
  const getRequestSagaArgs = compose(castArray, requestArgsCreator)

  function * saga(action) {
    try {
      const requestArgs = getRequestSagaArgs(action)
      yield put(routine.request())

      const result = yield * requestSaga(...requestArgs)

      yield put(routine.success(result))
      if (onSuccessSaga) {
        yield * onSuccessSaga(result)
      }
    } catch (error) {
      yield put(routine.failure(error))
    } finally {
      yield put(routine.fulfill())
    }
  }

  function * watcher() {
    switch (watcherMode) {
      case SAGA_WATCHER_ALL_MODE:
        yield takeEvery(routine.TRIGGER, saga)
        break
      case SAGA_WATCHER_LATEST_MODE:
        yield takeLatest(routine.TRIGGER, saga)
        break
      default:
        throw new Error(`watcherMode ${watcherMode} is not implemented in utils/saga#createRoutineSaga`)
    }
  }

  return {
    saga,
    watcher
  }
}

/**
 * creates authed api from state token
 *
 * @param {String -> Object} apiCreator - authed api by token creator
 * @param {state -> Strng} [tokenSelector=Login.selectToken] - redux token selector
 * @return {Object} authedApi object
 */
export function * createAuthedApiSaga(apiCreator, tokenSelector = selectLoginToken) {
  const token = yield select(tokenSelector)

  return apiCreator(token)
}
